import type { DiscoveryBranding, DiscoveryBrandingAsset } from '../services/discovery'
import { useAppState } from '../stores/AppState'
import i18n from '../utils/i18n'

const brandingIntialisationError =
  'Branding is not initialised. Check the branding has been correctly initialised.'

let store: ReturnType<typeof useAppState>
let branding: DiscoveryBranding | null = null
let fontColor: string = '#000000' // Default font color

// Initialize the store and branding
async function initialiseBranding() {
  store = useAppState()
  branding = await store.getBranding
}

async function getCompanyName(): Promise<string> {
  if (!branding) {
    console.error(brandingIntialisationError)
    return ''
  }

  if (branding.companyName) {
    return branding.companyName
  } else {
    return i18n.global.t('appName')
  }
}

async function getLogo(): Promise<string> {
  if (!branding) {
    console.error(brandingIntialisationError)
    return ''
  }
  if (branding.tenantAssets) {
    const logoAsset = branding.tenantAssets.find((asset) => asset.fileName === 'logo.png')
    if (logoAsset) {
      return `${logoAsset.path.endsWith('/') ? logoAsset.path.slice(0, -1) : logoAsset.path}/${logoAsset.fileName}`
    } else {
      return '/accessIcon.svg'
    }
  } else {
    return '/accessIcon.svg'
  }
}

async function setThemeValues() {
  if (!branding) {
    console.error(brandingIntialisationError)
    return
  }

  document.title = await getCompanyName()

  if (branding.tenantAssets) {
    //TODO: This is not working in the browser console
    const favIconAsset: DiscoveryBrandingAsset | undefined = branding.tenantAssets.find(
      (asset) => asset.fileName === 'favicon.ico'
    )
    if (favIconAsset) {
      updateFavicon(
        `${favIconAsset.path.endsWith('/') ? favIconAsset.path.slice(0, -1) : favIconAsset.path}/${favIconAsset.fileName}`
      )
    }
  }

  if (branding.theme) {
    const themeJson = JSON.parse(branding.theme)
    document.documentElement.style.setProperty('--icon-color', themeJson.IconColour)
    document.documentElement.style.setProperty('--base-icon-color', themeJson.IconTileColour)
    document.documentElement.style.setProperty('--accent-color', themeJson.ThemeColour)
    document.documentElement.style.setProperty('--accent-color-rgb', themeJson.ThemeColour)

    // Convert hex to RGB
    const rgb = hexToRgb(themeJson.ThemeColour)
    if (rgb) {
      const rgbString = `${rgb.r}, ${rgb.g}, ${rgb.b}`
      document.documentElement.style.setProperty('--accent-color-rgb', rgbString)

      // Calculate luminance
      const luminance = calculateLuminance(rgb.r, rgb.g, rgb.b)
      fontColor = luminance < 0.3 ? '#ffffff' : '#000000' // Threshold of 0.5
      document.documentElement.style.setProperty('--data-table-header-text-color', fontColor)
    }
  }
}

function updateFavicon(faviconUrl: string) {
  let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']")
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = faviconUrl
}

function calculateLuminance(r: number, g: number, b: number): number {
  const a = [r, g, b].map((v) => {
    v /= 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '')

  // Parse the r, g, b values
  let bigint
  if (hex.length === 3) {
    bigint = parseInt(
      hex
        .split('')
        .map((c) => c + c)
        .join(''),
      16
    )
  } else if (hex.length === 6) {
    bigint = parseInt(hex, 16)
  } else {
    return null // Invalid hex color
  }

  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return { r, g, b }
}

export { initialiseBranding, setThemeValues, getCompanyName, getLogo, fontColor }
